'use client'

import { useEffect, useRef } from 'react'

/**
 * Calls `onFocus` whenever the tab becomes visible again or the window
 * regains focus — so a card that fetched ONCE on mount self-heals when the
 * user returns to a long-open tab (e.g. across the midnight day boundary,
 * where the Daily Challenge would otherwise keep showing a question the user
 * already answered elsewhere).
 *
 * - Fires only when the document is actually visible (ignores the hide half
 *   of `visibilitychange`).
 * - Also fires on `pageshow` when the page is restored from the back/forward
 *   cache (`event.persisted`) — hitting the browser Back button restores a
 *   frozen DOM snapshot that no `router.push` re-render would touch, which is
 *   the classic "I did some work, went back, and the page is stale" bug.
 * - Throttled by `minIntervalMs` (default 30s) so rapid alt-tabbing doesn't
 *   spam the endpoint. `lastRef` starts at 0, so the FIRST return always
 *   fires immediately.
 * - `enabled` lets a caller pause it (e.g. while a submit is in flight).
 * - Callback + enabled are held in refs, so the listeners attach once and
 *   don't churn as the caller re-renders.
 */
export function useRefetchOnFocus(
  onFocus: () => void,
  opts: { enabled?: boolean; minIntervalMs?: number } = {},
): void {
  const { enabled = true, minIntervalMs = 30_000 } = opts

  const cbRef = useRef(onFocus)
  cbRef.current = onFocus
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled
  const lastRef = useRef(0)

  useEffect(() => {
    const handler = () => {
      if (!enabledRef.current) return
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - lastRef.current < minIntervalMs) return
      lastRef.current = now
      cbRef.current()
    }
    // bfcache restore (browser Back): fire regardless of visibility state, and
    // bypass the throttle — a restored snapshot is stale by definition.
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted || !enabledRef.current) return
      lastRef.current = Date.now()
      cbRef.current()
    }
    document.addEventListener('visibilitychange', handler)
    window.addEventListener('focus', handler)
    window.addEventListener('pageshow', onPageShow)
    return () => {
      document.removeEventListener('visibilitychange', handler)
      window.removeEventListener('focus', handler)
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [minIntervalMs])
}
